import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  FileText, Loader2, Plus, X, CheckCircle, Clock, User, Calendar, Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ItemCodeRequestsRegisterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeExists, setCodeExists] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  // Handle request_id from query param (when redirected from My Tasks)
  useEffect(() => {
    const requestId = searchParams.get('request_id');
    if (requestId && requests.length > 0 && !showModal) {
      const request = requests.find(r => r.id === requestId);
      if (request) {
        setSelectedRequest(request);
        setNewCode('');
        setCodeExists(null);
        setShowModal(true);
        // Clear the query param after opening modal
        setSearchParams({});
      }
    }
  }, [requests, searchParams, showModal, setSearchParams]);

  const fetchRequests = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/item-code-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch requests');
      const data = await res.json();
      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = (request) => {
    setSelectedRequest(request);
    setNewCode('');
    setCodeExists(null);
    setShowModal(true);
  };

  const checkCodeDuplicate = async (code) => {
    if (!code.trim()) {
      setCodeExists(null);
      return;
    }

    const token = localStorage.getItem('token');
    setCheckingCode(true);
    try {
      const res = await fetch(`${API}/api/item-code-requests/${selectedRequest.id}/check-code?code=${encodeURIComponent(code)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCodeExists(data);
    } catch (error) {
      console.error('Error checking code:', error);
    } finally {
      setCheckingCode(false);
    }
  };

  const handleSubmit = async (action) => {
    // action: "create" or "exists"
    if (action === 'create' && !newCode.trim()) {
      toast.error('Please enter an item code');
      return;
    }

    const token = localStorage.getItem('token');
    setProcessingId(selectedRequest.id);
    try {
      const res = await fetch(`${API}/api/item-code-requests/${selectedRequest.id}/process?item_code=${encodeURIComponent(newCode)}&action=${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to process request');
      }

      const data = await res.json();
      toast.success(data.message);
      setShowModal(false);
      fetchRequests();
    } catch (error) {
      console.error('Error processing request:', error);
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Item Code Requests Register
            </h1>
            <p className="text-sm text-gray-500 mt-1">Process pending item code creation requests</p>
          </div>
          <Badge variant="outline" className="text-sm">
            {requests.length} Pending
          </Badge>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No pending requests</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start gap-6">
                    {/* Left: Request Details */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs font-mono">
                          {request.request_number}
                        </Badge>
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(request.created_at)}
                        </span>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          {request.generic_name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{request.specs}</p>
                      </div>

                      {request.has_variants && request.variant_fields && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                          <p className="text-xs font-medium text-amber-700 mb-1">Variants:</p>
                          <p className="text-xs text-amber-600 whitespace-pre-wrap">{request.variant_fields}</p>
                        </div>
                      )}

                      {request.remarks && (
                        <p className="text-sm text-gray-500 italic">{request.remarks}</p>
                      )}

                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <User className="w-3.5 h-3.5" />
                        Requested by: {request.requested_by_name}
                      </div>
                    </div>

                    {/* Right: Action Button */}
                    <div>
                      <Button
                        onClick={() => handleCreateCode(request)}
                        disabled={processingId === request.id}
                        className="gap-2"
                      >
                        {processingId === request.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            Create Code
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Code Modal */}
        {showModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="pb-4 border-b">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">Create Item Code</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowModal(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Request Details (Read-only) */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Request Number</label>
                    <p className="text-sm font-mono">{selectedRequest.request_number}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Generic Name</label>
                    <p className="text-sm font-medium">{selectedRequest.generic_name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Specifications</label>
                    <p className="text-sm text-gray-700">{selectedRequest.specs}</p>
                  </div>
                  {selectedRequest.has_variants && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Variants</label>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedRequest.variant_fields}</p>
                    </div>
                  )}
                </div>

                {/* New Code Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Item Code <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newCode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setNewCode(value);
                      if (value.length >= 3) {
                        checkCodeDuplicate(value);
                      } else {
                        setCodeExists(null);
                      }
                    }}
                    placeholder="Enter new item code"
                    className="text-sm"
                  />
                  {checkingCode && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Checking...
                    </p>
                  )}
                  {codeExists && codeExists.exists && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <X className="w-3 h-3" />
                      {codeExists.message}
                    </p>
                  )}
                  {codeExists && !codeExists.exists && newCode.length >= 3 && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {codeExists.message}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => handleSubmit('create')}
                    disabled={processingId || !newCode.trim() || (codeExists && codeExists.exists)}
                    className="flex-1 gap-2"
                  >
                    {processingId ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Create New Code
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('exists')}
                    disabled={processingId}
                    variant="outline"
                    className="flex-1 gap-2"
                  >
                    {processingId ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4" />
                        Code Already Exists
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  Create new code if it doesn't exist, or mark as "Already Exists" if the code is already in the system
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ItemCodeRequestsRegisterPage;
